// Reunderwriting Module (Tier 3) PR 3 — case-detail page.
//
// Cloned from pages/deals/DealDetailPage. Differences:
//   - No Intake / Pre-Approval tabs (Reunderwriting cases are born into
//     Underwriting per spec §3.4).
//   - Underwriting / Financial / Legal / Business Analysis tabs render
//     the criteria checklist + lane Pass/Fail + combined-outcome panel.
//   - Closing tab fires the PR 2 downstream-loan-effect approval flow.
//   - Header carries the 7-pill ReunderwritingAssignmentPills widget,
//     the subject-loan deep link, and the launch reason.

import { useCallback, useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Gavel,
  Handshake,
  Landmark,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import {
  Badge,
  Card,
  RECORD_RAIL_WIDTH,
  ResponsiveSplit,
} from "@/design-system/components/ui";
import {
  CROSS_CUTTING_TAB_ICONS,
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { ReunderwritingAnalystTab } from "@/components/reunderwriting/ReunderwritingAnalystTab";
import { ReunderwritingAssignmentPills } from "@/components/reunderwriting/ReunderwritingAssignmentPills";
import { ReunderwritingClosingTab } from "@/components/reunderwriting/ReunderwritingClosingTab";
import {
  useAdvanceToClosing,
  useReunderwritingCase,
} from "@/hooks/reunderwriting/useReunderwriting";
import { useReunderwritingV1 } from "@/hooks/reunderwriting/useFeatureFlag";
import {
  REUNDERWRITING_STAGE_LABEL,
  REUNDERWRITING_SUB_STATE_LABEL,
  type ReunderwritingCaseDto,
} from "@/types/reunderwriting";
import { useDateFormatter } from "@/utils/datetime";

type TabId =
  | "overview"
  | "underwriting"
  | "financial-analysis"
  | "legal-analysis"
  | "business-analysis"
  | "closing"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabId[] = [
  "overview",
  "underwriting",
  "financial-analysis",
  "legal-analysis",
  "business-analysis",
  "closing",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
] as const;

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabId>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  { key: "underwriting", label: "Underwriting", icon: ShieldCheck },
  { key: "financial-analysis", label: "Financial Analysis", icon: TrendingUp },
  { key: "legal-analysis", label: "Legal Analysis", icon: Gavel },
  { key: "business-analysis", label: "Business Analysis", icon: Landmark },
  { key: "closing", label: "Closing", icon: Handshake },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
] as const;

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function ReunderwritingCaseDetailPage() {
  const navigate = useNavigate();
  const flagOn = useReunderwritingV1();
  const { caseId } = useParams<{ caseId: string }>();
  const caseQuery = useReunderwritingCase(caseId);
  const rcase = caseQuery.data;

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabId = isTabId(tabParam) ? tabParam : "overview";

  const setTab = useCallback(
    (next: TabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      if (t.isContentEditable) return true;
      const tag = t.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    }
    function handler(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const idx = Number(e.key);
      if (Number.isInteger(idx) && idx >= 1 && idx <= TABS.length) {
        e.preventDefault();
        setTab(TABS[idx - 1]);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setTab]);

  if (!flagOn) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Reunderwriting disabled</h2>
        <p className="text-small text-gray-mid">
          The Reunderwriting module is not enabled in this environment.
        </p>
      </Card>
    );
  }

  if (caseQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading case…</p>
      </Card>
    );
  }
  if (!rcase) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Case not found</h2>
        <p className="text-small text-gray-mid">
          The reunderwriting case you requested was not found or you don&apos;t
          have permission to view it.
        </p>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate("/reunderwriting/pipeline")}
        >
          Back to pipeline
        </button>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-3" data-testid="reunderwriting-case-detail">
      <CaseHeader rcase={rcase} />

      <EntityTabBar
        ariaLabel="Reunderwriting case sections"
        testIdPrefix="reunderwriting"
        items={TAB_ITEMS.map((it) => ({ ...it, badge: null }))}
        value={tab}
        onChange={setTab}
      />

      <div className="pt-space-4">
        {tab === "overview" && <OverviewTab rcase={rcase} />}
        {tab === "underwriting" && <ReunderwritingAnalystTab rcase={rcase} lane="A1" />}
        {tab === "financial-analysis" && <ReunderwritingAnalystTab rcase={rcase} lane="A2" />}
        {tab === "legal-analysis" && <ReunderwritingAnalystTab rcase={rcase} lane="A3" />}
        {tab === "business-analysis" && <ReunderwritingAnalystTab rcase={rcase} lane="A4" />}
        {tab === "closing" && <ReunderwritingClosingTab rcase={rcase} />}
        {tab === "documents" && (
          <DocumentPanel
            ownerType="ReunderwritingCase"
            ownerId={rcase.id}
            rootName={rcase.caseNumber}
          />
        )}
        {tab === "tasks" && (
          <TaskListMini
            relatedEntityType="ReunderwritingCase"
            relatedEntityId={rcase.id}
            addButtonLabel="+ Add task linked to this case"
          />
        )}
        {tab === "calendar" && (
          <AppointmentListMini
            relatedEntityType="ReunderwritingCase"
            relatedEntityId={rcase.id}
            entityLabel={rcase.caseNumber}
          />
        )}
        {tab === "messages" && (
          <RelatedConversationsPanel
            resourceType="reunderwriting-case"
            resourceId={rcase.id}
            entityLabel={rcase.caseNumber}
          />
        )}
        {tab === "communications" && (
          <CommunicationsTabPanel
            ownerType="reunderwriting-case"
            ownerId={rcase.id}
            ownerLabel={rcase.caseNumber}
          />
        )}
        {tab === "notes" && (
          <NotesTabPanel ownerType="reunderwriting-case" ownerId={rcase.id} />
        )}
        {tab === "activity" && (
          <ActivityTabPanel ownerType="reunderwriting-case" ownerId={rcase.id} />
        )}
      </div>
    </div>
  );
}

function CaseHeader({ rcase }: { rcase: ReunderwritingCaseDto }) {
  const advance = useAdvanceToClosing(rcase.id);
  const toast = useToast();

  async function onAdvance() {
    try {
      await advance.mutateAsync();
      toast.success("Case advanced to Closing.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-space-3">
        <div className="min-w-0">
          <p className="text-label uppercase tracking-label text-gold-shadow m-0">
            Reunderwriting · {rcase.eventTypeCode}
          </p>
          <h1 className="font-display text-h1 m-0">{rcase.caseNumber}</h1>
          <p className="text-small text-gray-mid m-0 mt-space-1">
            Subject loan:{" "}
            <Link
              to={`/portfolio-admin/loans/${rcase.subjectLoanId}`}
              className="text-gold-shadow hover:underline"
              data-testid="reunderwriting-subject-loan-link"
            >
              {rcase.subjectLoanNumber ?? rcase.subjectLoanId}
            </Link>
            {" · "}Subject party kind: {rcase.subjectCounterpartyKind}
          </p>
          {rcase.launchReason && (
            <p className="text-caption text-gray-mid italic m-0 mt-space-1">
              Launch reason: {rcase.launchReason}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-space-2">
          <div className="flex items-center gap-space-2">
            <Badge status={rcase.stage === "Closed" ? "finalized" : "gold"}>
              {REUNDERWRITING_STAGE_LABEL[rcase.stage]}
            </Badge>
            <Badge status="archived">
              {REUNDERWRITING_SUB_STATE_LABEL[rcase.subState]}
            </Badge>
          </div>
          <ReunderwritingAssignmentPills caseId={rcase.id} stage={rcase.stage} />
          {rcase.stage === "Underwriting" && (
            <button
              type="button"
              onClick={onAdvance}
              disabled={advance.isPending}
              className="text-small text-gold-shadow hover:underline disabled:opacity-40"
              data-testid="reunderwriting-advance-to-closing"
            >
              Advance to Closing →
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

function OverviewTab({ rcase }: { rcase: ReunderwritingCaseDto }) {
  const { dateTime } = useDateFormatter();
  return (
    // Container-driven — Table & Tile Standard §12. This was
    // `lg:grid-cols-3` + `lg:col-span-2`, which asks the WINDOW how wide
    // this box is; the window cannot answer that once the box is a tab
    // body, a drawer or a split pane.
    <ResponsiveSplit
      gap="md"
      mainMinWidth="34rem"
      railWidth={RECORD_RAIL_WIDTH}
      railGrows={false}
      main={
        <div className="flex flex-col gap-space-3">
          <Card>
            <h3 className="font-display text-h3 m-0">Case overview</h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 mt-space-3">
              <Detail label="Case number" value={rcase.caseNumber} />
              <Detail label="Event type" value={rcase.eventTypeCode} />
              <Detail label="Stage" value={REUNDERWRITING_STAGE_LABEL[rcase.stage]} />
              <Detail label="Sub-state" value={REUNDERWRITING_SUB_STATE_LABEL[rcase.subState]} />
              <Detail
                label="Subject loan"
                value={
                  <Link
                    to={`/portfolio-admin/loans/${rcase.subjectLoanId}`}
                    className="text-gold-shadow hover:underline"
                  >
                    {rcase.subjectLoanNumber ?? rcase.subjectLoanId}
                  </Link>
                }
              />
              <Detail
                label="Subject party kind"
                value={rcase.subjectCounterpartyKind}
              />
              <Detail
                label="Assigned A1"
                value={rcase.assignedA1Id ?? "—"}
              />
              <Detail
                label="Stage entered"
                value={dateTime(rcase.stageEnteredDate)}
              />
            </dl>
            {rcase.launchReason && (
              <div className="mt-space-3">
                <p className="text-label uppercase tracking-label text-gold-shadow m-0 mb-space-1">
                  Launch reason
                </p>
                <p className="text-small m-0">{rcase.launchReason}</p>
              </div>
            )}
            {rcase.createdLoanId && (
              <div className="mt-space-3">
                <p className="text-label uppercase tracking-label text-gold-shadow m-0 mb-space-1">
                  Resulting loan
                </p>
                <Link
                  to={`/portfolio-admin/loans/${rcase.createdLoanId}`}
                  className="text-small text-gold-shadow hover:underline"
                >
                  {rcase.createdLoanNumber ?? rcase.createdLoanId}
                </Link>
              </div>
            )}
          </Card>
      </div>
      }
      rail={
        <div className="grid gap-space-4 min-w-0">
        <div className="flex flex-col gap-space-3">
          <Card>
            <h3 className="font-display text-h3 m-0">Cross-links</h3>
            <ul className="m-0 mt-space-2 p-0 list-none flex flex-col gap-space-2 text-small">
              <li>
                →{" "}
                <Link
                  to={`/portfolio-admin/loans/${rcase.subjectLoanId}?tab=review`}
                  className="text-gold-shadow hover:underline"
                >
                  Subject loan&apos;s Review timeline
                </Link>
              </li>
              <li>
                →{" "}
                <Link
                  to="/reunderwriting/pipeline"
                  className="text-gold-shadow hover:underline"
                >
                  Reunderwriting pipeline
                </Link>
              </li>
            </ul>
          </Card>
        </div>
    </div>
      }
    />
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-label uppercase tracking-label text-gold-shadow m-0">
        {label}
      </dt>
      <dd className="text-small text-onyx m-0 mt-space-1">{value}</dd>
    </div>
  );
}
